import React from 'react';
import { formatDuration } from '../utils/format';

export interface ProgressBarProps {
  /** Nombre total de modules du cours */
  totalModules?: number;
  /** Nombre de modules terminés par l'apprenant */
  completedModules?: number;
  /** Pourcentage déjà calculé (prioritaire s'il est fourni et que les compteurs sont absents) */
  progressPercentage?: number;
  /** Temps passé (secondes) — affiché si showTime */
  timeSpentSeconds?: number;
  /** Afficher la ligne de libellé « X % — n/N modules terminés » */
  showLabel?: boolean;
  /** Afficher le temps passé à droite du libellé */
  showTime?: boolean;
  /** Hauteur de la barre */
  size?: 'sm' | 'md';
  className?: string;
}

const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
};

/**
 * Barre de progression apprenant (sprint « traçabilité apprenants » — 01/10/2026).
 * Couleurs de la charte Kayliss : cyan #00bfff → bleu #0066cc.
 * Accessible : role="progressbar" + aria-valuenow/min/max et libellé explicite.
 */
const ProgressBar: React.FC<ProgressBarProps> = ({
  totalModules,
  completedModules,
  progressPercentage,
  timeSpentSeconds,
  showLabel = true,
  showTime = false,
  size = 'md',
  className = ''
}) => {
  const hasCounts = typeof totalModules === 'number' && typeof completedModules === 'number';
  const computed = hasCounts && totalModules! > 0
    ? (completedModules! / totalModules!) * 100
    : (typeof progressPercentage === 'number' ? progressPercentage : 0);
  const percentage = clampPercent(computed);

  const label = hasCounts
    ? `${percentage} % — ${completedModules}/${totalModules} modules terminés`
    : `${percentage} % de progression`;

  const barHeight = size === 'sm' ? 'h-1.5' : 'h-2';

  return (
    <div className={className}>
      {showLabel && (
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="font-medium text-gray-700">{label}</span>
          {showTime && typeof timeSpentSeconds === 'number' && (
            <span className="text-gray-500">Temps passé : {formatDuration(timeSpentSeconds)}</span>
          )}
        </div>
      )}
      <div
        className={`w-full ${barHeight} bg-gray-200 rounded-full overflow-hidden`}
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        title={label}
      >
        <div
          className={`${barHeight} rounded-full transition-all duration-300`}
          style={{
            width: `${percentage}%`,
            background: 'linear-gradient(90deg, #00bfff 0%, #0066cc 100%)'
          }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
